import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Check, Copy, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { parseLabelValue } from '@/utils/labelUtils';
import { courseService } from '@/services/api';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface OrganizationDialogProps {
  isCreateOpen: boolean;
  isEditOpen: boolean;
  isGenerateInviteOpen: boolean;
  newOrg: {
    name: string;
    description: string;
    labels?: string[];
  };
  inviteKey: string;
  copied: boolean;
  onCreateOrgClose: () => void;
  onEditOrgClose: () => void;
  onGenerateInviteClose: () => void;
  onNewOrgChange: (data: { name: string; description: string; labels?: string[] }) => void;
  onCreateOrg: () => void;
  onUpdateOrg: () => void;
  onCopyInvite: () => void;
}

const OrganizationDialogs: React.FC<OrganizationDialogProps> = ({
  isCreateOpen,
  isEditOpen,
  isGenerateInviteOpen,
  newOrg,
  inviteKey,
  copied,
  onCreateOrgClose,
  onEditOrgClose,
  onGenerateInviteClose,
  onNewOrgChange,
  onCreateOrg,
  onUpdateOrg,
  onCopyInvite
}) => {
  const [newTag, setNewTag] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Fetch available course tags when the component mounts
  useEffect(() => {
    const fetchTags = async () => {
      if (isCreateOpen || isEditOpen) {
        setIsLoadingTags(true);
        try {
          const labels = await courseService.getLabels();
          setAvailableTags(Array.isArray(labels) ? labels : []);
        } catch (error) {
          console.error('Error fetching course labels:', error);
        } finally {
          setIsLoadingTags(false);
        }
      }
    };

    fetchTags();
  }, [isCreateOpen, isEditOpen]);

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    const tag = newTag.trim();
    const labels = [...(newOrg.labels || [])];
    
    if (!labels.includes(tag)) {
      onNewOrgChange({ ...newOrg, labels: [...labels, tag] });
      setNewTag('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    const labels = [...(newOrg.labels || [])];
    const updatedLabels = labels.filter(tag => tag !== tagToRemove);
    onNewOrgChange({ ...newOrg, labels: updatedLabels });
  };
  
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Add a tag from the dropdown of available course tags
  const handleSelectTag = (value: string) => {
    if (!value) return;
    
    const labels = [...(newOrg.labels || [])];
    if (!labels.includes(value)) {
      onNewOrgChange({ ...newOrg, labels: [...labels, value] });
    }
  };

  // Enhanced tag section with course tag dropdown
  const TagsSection = () => (
    <div className="space-y-2">
      <Label>Tags</Label>
      <div className="flex flex-wrap gap-2 mb-3">
        {(newOrg.labels || []).map((tag, index) => (
          <Badge key={index} variant="secondary" className="flex items-center gap-1">
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {(newOrg.labels || []).length === 0 && (
          <span className="text-sm text-muted-foreground">No tags added yet</span>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <Label className="mb-1.5 block text-sm">Select from available course tags</Label>
          {isLoadingTags ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Select onValueChange={handleSelectTag}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a course tag" />
              </SelectTrigger>
              <SelectContent>
                {availableTags.length > 0 ? (
                  availableTags
                    .filter(tag => !(newOrg.labels || []).includes(tag))
                    .map(tag => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))
                ) : (
                  <SelectItem value="no-tags" disabled>
                    No available course tags found
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground mt-2">
        Tags determine which courses this organization's members can access.
      </p>
    </div>
  );

  return (
    <>
      {/* Create Organization Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={onCreateOrgClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Add the details for your new organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name *</Label>
              <Input
                id="name"
                placeholder="Enter organization name"
                value={newOrg.name}
                onChange={(e) => onNewOrgChange({ ...newOrg, name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter organization description"
                rows={3}
                value={newOrg.description}
                onChange={(e) => onNewOrgChange({ ...newOrg, description: e.target.value })}
              />
            </div>
            
            <TagsSection />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={onCreateOrgClose}>
              Cancel
            </Button>
            <Button onClick={onCreateOrg}>Create Organization</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Organization Dialog */}
      <Dialog open={isEditOpen} onOpenChange={onEditOrgClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update the details for this organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Organization Name *</Label>
              <Input
                id="edit-name"
                placeholder="Enter organization name"
                value={newOrg.name}
                onChange={(e) => onNewOrgChange({ ...newOrg, name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter organization description"
                rows={3}
                value={newOrg.description}
                onChange={(e) => onNewOrgChange({ ...newOrg, description: e.target.value })}
              />
            </div>
            
            <TagsSection />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={onEditOrgClose}>
              Cancel
            </Button>
            <Button onClick={onUpdateOrg}>Update Organization</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Generate Invite Dialog */}
      <Dialog open={isGenerateInviteOpen} onOpenChange={onGenerateInviteClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invitation Key</DialogTitle>
            <DialogDescription>
              Share this key with users you want to invite to the organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Input
                value={inviteKey}
                readOnly
                className="pr-10"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                onClick={onCopyInvite}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                <span className="sr-only">Copy</span>
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This invitation key will allow users to join the organization when signing up.
              For security, it can only be used once and expires after 30 days.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={onGenerateInviteClose}>
              Close
            </Button>
            <Button onClick={onCopyInvite}>
              {copied ? 'Copied!' : 'Copy Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrganizationDialogs;
